import { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { TilesRenderer } from '3d-tiles-renderer';
import {
  GoogleCloudAuthPlugin,
  GLTFExtensionsPlugin,
  ReorientationPlugin,
  UnloadTilesPlugin,
} from '3d-tiles-renderer/plugins';
import { setPhotorealisticTilesGroup } from '../../services/photorealisticTilesRegistry';
import {
  TILE_ERROR_ESCALATE_COUNT,
  applyTileSnapshotBudget,
  getTileSnapshotBudget,
  isHeapUnderPressure,
} from '../../services/photorealisticLoadLadder';

/**
 * Google Photorealistic 3D Tiles (Map Tiles API), recentered on lat/lng.
 * Attach tiles.group to the THREE scene directly — do not parent under an R3F
 * <group>, which will wipe manually-added children on reconcile.
 *
 * Pass Elevation API ASL as ReorientationPlugin `height` so the ground sits
 * near local Y≈0. Floor raycast in SitePlannerViewer still locks residual
 * seating. Snapshot budget + escalate: docs/PHOTOREALISTIC_MOBILE_FALLBACK.md
 */
function GooglePhotorealisticTiles({
  lat,
  lng,
  height = 0,
  enabled,
  /** Index into photorealisticLoadLadder budgets (0=full, 1=half, 2=quarter). */
  snapshotLevel = 0,
  onCredits,
  onReady,
  /** Fired once per attempt when tiles look unrecoverable (errors / heap). */
  onAttemptFailed,
}) {
  const tilesRef = useRef(null);
  const { camera, gl, scene } = useThree();
  const onCreditsRef = useRef(onCredits);
  const onReadyRef = useRef(onReady);
  const onAttemptFailedRef = useRef(onAttemptFailed);
  onCreditsRef.current = onCredits;
  onReadyRef.current = onReady;
  onAttemptFailedRef.current = onAttemptFailed;

  useEffect(() => {
    if (!enabled || lat == null || lng == null) return undefined;

    const apiToken = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiToken) return undefined;

    let disposed = false;
    let readyFired = false;
    let failedFired = false;
    const reorientHeight = Number.isFinite(Number(height)) ? Number(height) : 0;
    const budget = getTileSnapshotBudget(snapshotLevel);

    const fail = (reason) => {
      if (disposed || failedFired) return;
      failedFired = true;
      onAttemptFailedRef.current?.({ reason, snapshotLevel, budgetId: budget.id });
    };

    const tiles = new TilesRenderer();
    // Disable plugin "recommended" SSE so our ladder budget owns errorTarget.
    tiles.registerPlugin(
      new GoogleCloudAuthPlugin({ apiToken, useRecommendedSettings: false })
    );
    tiles.registerPlugin(new GLTFExtensionsPlugin({ metadata: true, rtc: true }));
    tiles.registerPlugin(
      new ReorientationPlugin({
        lat: (lat * Math.PI) / 180,
        lon: (lng * Math.PI) / 180,
        height: reorientHeight,
        recenter: true,
      })
    );
    const unloadPlugin = new UnloadTilesPlugin({ delay: 0 });
    tiles.registerPlugin(unloadPlugin);
    applyTileSnapshotBudget(tiles, budget, { unloadPlugin });

    tiles.group.userData.photorealisticTiles = true;
    tiles.group.userData.tileSnapshotId = budget.id;
    tilesRef.current = tiles;
    scene.add(tiles.group);
    setPhotorealisticTilesGroup(tiles.group);

    const publishCredits = () => {
      if (disposed) return;
      try {
        const attrs = tiles.getAttributions?.() || [];
        const text = attrs
          .filter((a) => a.type === 'string' && a.value)
          .map((a) => a.value)
          .join(' · ');
        onCreditsRef.current?.(text || 'Google');
      } catch {
        onCreditsRef.current?.('Google');
      }
    };

    const onTileSet = () => {
      publishCredits();
      if (!readyFired) {
        readyFired = true;
        requestAnimationFrame(() => onReadyRef.current?.());
      }
    };

    const onPressureTick = () => {
      if (isHeapUnderPressure()) {
        fail('heap-pressure');
        return;
      }
      const failed = Number(tiles.stats?.failed) || 0;
      if (failed >= TILE_ERROR_ESCALATE_COUNT) {
        fail('tile-errors');
      }
    };
    const pressureId = window.setInterval(onPressureTick, 2000);

    tiles.addEventListener('load-tile-set', onTileSet);
    tiles.addEventListener('tile-visibility-change', publishCredits);

    return () => {
      disposed = true;
      window.clearInterval(pressureId);
      tiles.removeEventListener('load-tile-set', onTileSet);
      tiles.removeEventListener('tile-visibility-change', publishCredits);
      scene.remove(tiles.group);
      tiles.dispose();
      tilesRef.current = null;
      setPhotorealisticTilesGroup(null);
      onCreditsRef.current?.('');
    };
  }, [enabled, lat, lng, height, scene, snapshotLevel]);

  useFrame(() => {
    const tiles = tilesRef.current;
    if (!tiles || !enabled) return;
    camera.updateMatrixWorld();
    tiles.setCamera(camera);
    tiles.setResolutionFromRenderer(camera, gl);
    tiles.update();
  });

  return null;
}

export default GooglePhotorealisticTiles;
